import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import type { MetaContent } from "@@/shared/meta-content/meta-content";
import type { MinMaxRange } from "@@/utilities/set-within-range";
import type { Except } from "type-fest";
import type { ResourcePrice } from "../resource-price/resource-price";
import type { ResourceReferenceId } from "../resource-references/resource-reference-id";
import type { ResourceId } from "../resources/resource-id";
import type { BookableOption } from "./bookable-option";
import type { BookableOptionId } from "./bookable-option-id";
import { bookableOptionIdFactory } from "./bookable-option-id";

type QuantityPickerBookableOption = {
    readonly _id: BookableOptionId;
    readonly _type: "QUANTITY_PICKER_BOOKABLE_OPTION";
    readonly _createdAt: IsoAndUnixTimestamp;

    readonly resourceReferenceId: ResourceReferenceId;
    readonly resourceId: ResourceId;
    readonly overridesId?: string;
    readonly meta: MetaContent;
    readonly resourcePrice: ResourcePrice;
    readonly minMax: MinMaxRange;
    readonly default: number;
    readonly options: BookableOption[];
};

type quantityPickerBookableOptionFactoryParams = Except<
    QuantityPickerBookableOption,
    "_id" | "_type" | "_createdAt"
> & { _id?: BookableOptionId };

const quantityPickerBookableOptionFactory = (
    params: quantityPickerBookableOptionFactoryParams,
): QuantityPickerBookableOption => {
    return {
        ...params,
        _id: params._id || bookableOptionIdFactory(),
        _type: "QUANTITY_PICKER_BOOKABLE_OPTION",
        _createdAt: nowIsoAndUnix(),
    };
};

const isQuantityPickerBookableOption = (
    item:
        | {
              _id: BookableOptionId;
              _type: unknown;
          }
        | undefined,
): item is QuantityPickerBookableOption => {
    if (typeof item === "undefined") return false;
    return item._type === "QUANTITY_PICKER_BOOKABLE_OPTION";
};

export { isQuantityPickerBookableOption, quantityPickerBookableOptionFactory };
export type { QuantityPickerBookableOption };
